import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useWorkspaceReadOnly } from './context';

/**
 * Visually disables an interactive region when the workspace is read-only
 * (ENG-1071's belt-and-suspenders UI gate — see `src/features/
 * workspace-readonly/AGENTS.md` for the full list of gated components and the
 * mutation-hook guards this pairs with). Not read-only (the default, and
 * always true in-app) renders the identical `<div className={className}>` —
 * just without the disabled styling — so wrapping an existing region costs
 * nothing there.
 *
 * ALWAYS renders a real `<div>` (never a bare fragment in either branch) —
 * deliberately, so `className` (e.g. a `space-y-*` layout utility the wrapped
 * content relies on for spacing between its children) is applied
 * consistently in both modes. An earlier version of this component rendered
 * `<>{children}</>` when not read-only; that silently dropped `className`
 * entirely in the common (non-read-only) case, breaking every existing
 * caller's spacing the moment this wrapper was introduced. Callers should
 * pass whatever layout classes the wrapped content needs directly to
 * `ReadOnlyGate` (as if it were the div they're replacing), not to some
 * separate always-present outer element.
 *
 * `aria-disabled` + `pointer-events-none` + `opacity-60` — NOT `disabled` (a
 * `div` can't be `disabled`; the individual inputs/buttons inside still need
 * their own `disabled` attribute for keyboard/AT users, since
 * `pointer-events-none` alone doesn't stop Enter/Space activation via focus).
 * Callers of leaf controls should still pass `disabled={useWorkspaceReadOnly()}`
 * directly where practical — this wrapper is the coarse, whole-region
 * backstop for the many controls that don't take one prop each.
 */
export function ReadOnlyGate({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const readOnly = useWorkspaceReadOnly();
  return (
    <div
      aria-disabled={readOnly || undefined}
      className={cn(readOnly && 'pointer-events-none opacity-60', className)}
    >
      {children}
    </div>
  );
}
